import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LogOut, Home, Users, Calendar, DollarSign, FolderOpen, User, Menu, X, Gift, Shield, Scale, Lightbulb, FileText, RefreshCw, AlertTriangle, CreditCard } from 'lucide-react';
import Footer from '@/components/Footer';
import NotificationBell from '@/components/NotificationBell';
import { apiClient } from '@/App';
import { toast } from 'sonner';

const LOGO_URL = "https://customer-assets-rejwkqb3.emergentagent.net/job_5700d295-d3ed-4629-8765-e98307778f89/artifacts/bbyq8lme_icone_verre_vert_512.png";

const Layout = ({ children, user, onLogout, onUpdate, isAdmin }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Vérifier si l'utilisateur est le Master Admin
  const isMasterAdmin = user?.email?.toLowerCase() === 'gmpdeveloppement@gmail.com';
  
  // Le Master Admin a-t-il une copropriété active ?
  const masterAdminInCopro = isMasterAdmin && user?.copropriete_active_id;

  // Fonction pour quitter une copropriété (Master Admin)
  const exitCopropriete = async () => {
    try {
      const response = await apiClient.post('/admin/exit-copropriete');
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
      }
      // Recharger l'utilisateur
      const userResponse = await apiClient.get('/auth/me');
      if (onUpdate) {
        onUpdate(userResponse.data);
      }
      localStorage.setItem('user', JSON.stringify(userResponse.data));
      toast.success('Retour au mode admin');
      navigate('/admin');
    } catch (error) {
      console.error('Erreur lors de la sortie:', error);
      toast.error('Erreur lors du retour au mode admin');
    }
  };

  // Navigation complète si le Master Admin est dans une copropriété, sinon vide pour aller à /admin
  // Récupérer le rôle de l'utilisateur dans la copropriété active
  const userRole = user?.role_par_copropriete?.[user?.copropriete_active_id] || '';
  const canManageImpayes = ['président', 'trésorier'].includes(userRole.toLowerCase()) || isMasterAdmin;
  
  const navigation = (isMasterAdmin && !masterAdminInCopro) ? [] : [
    { name: 'Tableau de bord', href: '/', icon: Home },
    { name: 'Copropriétaires', href: '/coproprietaires', icon: Users },
    { name: 'Calendrier', href: '/calendrier', icon: Calendar },
    { name: 'Appels de fonds', href: '/appels-fonds', icon: DollarSign },
    { name: 'Comptabilité', href: '/comptabilite', icon: DollarSign },
    { name: 'Assemblées Générales', href: '/assemblees', icon: FileText },
    { name: 'Propositions AG', href: '/sujets-ag', icon: Lightbulb },
    ...(canManageImpayes ? [{ name: 'Gérer les impayés', href: '/gestion-impayes', icon: AlertTriangle }] : []),
    ...(canManageImpayes ? [{ name: 'Historique paiements', href: '/historique-paiements', icon: CreditCard }] : []),
    { name: 'Assistant Juridique', href: '/assistant', icon: Scale },
    { name: 'Documents', href: '/documents', icon: FolderOpen },
  ];

  const closeMobileMenu = () => setMobileMenuOpen(false);

  // Badge gratuit
  const getSubscriptionBadge = () => {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 rounded-lg border border-emerald-200">
        <Gift className="w-4 h-4 text-emerald-600" />
        <span className="text-xs font-medium text-emerald-700">Gratuit</span>
      </div>
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 overflow-x-hidden">
      <div className="flex-1 flex overflow-x-hidden overflow-y-visible">
        {/* Overlay pour mobile */}
        {mobileMenuOpen && (
          <div 
            className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden"
            onClick={closeMobileMenu}
          />
        )}

        {/* Sidebar - Desktop + Mobile */}
        <aside className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-64 bg-white border-r border-slate-200 flex flex-col
          transform transition-transform duration-300 ease-in-out
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          max-h-screen
        `}>
          {/* Header avec logo */}
          <div className="p-3 lg:p-4 border-b border-slate-200 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <img 
                src={LOGO_URL} 
                alt="copropriete.app" 
                className="h-10 lg:h-12 w-auto rounded-lg"
              />
              <span className="text-lg font-bold text-slate-900 hidden lg:block">copropriete.app</span>
            </div>
            <button 
              onClick={closeMobileMenu}
              className="lg:hidden text-slate-600 hover:text-slate-900 p-2"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <nav className="flex-1 p-3 lg:p-4 space-y-1 overflow-y-auto min-h-0">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={closeMobileMenu}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`flex items-center gap-3 px-3 py-2.5 lg:py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-slate-900 text-white'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }`}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                  <span className="truncate">{item.name}</span>
                </Link>
              );
            })}
            
            {/* Bouton "Retour Admin" pour le Master Admin quand il est dans une copropriété */}
            {masterAdminInCopro && (
              <button
                onClick={() => {
                  closeMobileMenu();
                  exitCopropriete();
                }}
                data-testid="nav-retour-admin"
                className="flex items-center gap-3 px-3 py-2.5 lg:py-2 rounded-md text-sm font-medium transition-colors bg-amber-100 text-amber-800 hover:bg-amber-200 border border-amber-300 mt-4 w-full text-left"
              >
                <Shield className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                <span className="truncate">⬅ Retour Admin</span>
              </button>
            )}
            
            {/* Lien Admin pour le Master Admin (si pas dans une copro) */}
            {isAdmin && !masterAdminInCopro && (
              <Link
                to="/admin"
                onClick={closeMobileMenu}
                data-testid="nav-admin"
                className={`flex items-center gap-3 px-3 py-2.5 lg:py-2 rounded-md text-sm font-medium transition-colors ${
                  location.pathname === '/admin'
                    ? 'bg-amber-500 text-white'
                    : 'text-amber-600 hover:bg-amber-50 hover:text-amber-700 border border-amber-200'
                }`}
              >
                <Shield className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                <span className="truncate">Espace Admin</span>
              </Link>
            )}
          </nav>

          <div className="p-3 lg:p-4 border-t border-slate-200 flex-shrink-0 bg-white pb-20 lg:pb-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 lg:w-10 lg:h-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <User className="w-4 h-4 lg:w-5 lg:h-5 text-indigo-600" strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">
                  {user.prenom} {user.nom}
                </p>
                <p className="text-xs text-slate-500 truncate capitalize">
                  {isMasterAdmin ? 'Administrateur' : user.role}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-1">
              {!isMasterAdmin && (
                <>
                  <Link
                    to="/profil"
                    onClick={closeMobileMenu}
                    data-testid="nav-profil-link"
                    className="block w-full px-3 py-2 text-sm text-center text-slate-600 hover:bg-slate-100 rounded-md transition-colors"
                  >
                    Mon profil
                  </Link>
                  <Link
                    to="/selection-copropriete"
                    onClick={closeMobileMenu}
                    className="flex items-center justify-center gap-2 w-full px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-md transition-colors"
                  >
                    <RefreshCw className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
                    <span className="truncate">Changer de copropriété</span>
                  </Link>
                </>
              )}
              <button
                onClick={() => {
                  closeMobileMenu();
                  onLogout();
                }}
                data-testid="logout-button"
                className="flex items-center justify-center gap-2 w-full px-3 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
              >
                <LogOut className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
                Déconnexion
              </button>
            </div>
          </div>
        </aside>

        {/* Main content */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header mobile avec hamburger */}
          <div className="lg:hidden bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between sticky top-0 z-30">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="text-slate-600 hover:text-slate-900"
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-6 h-6" />
            </button>
            <img 
              src="https://customer-assets-rejwkqb3.emergentagent.net/job_5700d295-d3ed-4629-8765-e98307778f89/artifacts/bbyq8lme_icone_verre_vert_512.png" 
              alt="copropriete.app" 
              className="h-8 w-auto"
            />
            <NotificationBell />
          </div>

          {/* Header desktop avec notifications */}
          <div className="hidden lg:flex bg-white border-b border-slate-200 px-6 py-3 items-center justify-end sticky top-0 z-30">
            <NotificationBell />
          </div>

          {/* Contenu principal */}
          <main className="flex-1 overflow-auto pb-4">
            <div className="max-w-7xl mx-auto p-4 md:p-8">{children}</div>
          </main>
        </div>
      </div>
      
      {/* Footer - caché sur mobile dans l'app */}
      <div className="hidden lg:block">
        <Footer />
      </div>
    </div>
  );
};

export default Layout;